import React, { useState, useEffect } from "react";
import { Button as ReactstrapButton } from "reactstrap";
import { Button, Tag, Space, Badge, message, Modal, Input } from "antd";
import type { TableColumnsType } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkRequestAside from "./workRequestAside";
import {
  WorkRequestData,
  WorkRequestStatus,
  WorkRequestFilters
} from "./types";
import {
  getWorkRequests,
  updateWorkRequest
} from "./mockData/workRequestData";

const { TextArea } = Input;

const WorkRequestApprovals: React.FC = () => {
  const [sm, setSm] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingApprovals, setPendingApprovals] = useState<WorkRequestData[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<WorkRequestFilters>({
    statuses: [WorkRequestStatus.PendingApproval]
  });

  useEffect(() => {
    loadPendingApprovals();
  }, [filters]);

  const loadPendingApprovals = () => {
    setLoading(true);
    try {
      // Filter for pending approvals only
      const data: any = getWorkRequests({
        ...filters,
        statuses: [WorkRequestStatus.PendingApproval]
      });
      setPendingApprovals(data);
    } catch (error) {
      console.error("Error loading pending approvals:", error);
      message.error("Failed to load pending approvals");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: WorkRequestStatus): string => {
    const statusColors: { [key in WorkRequestStatus]: string } = {
      "Draft": "default",
      "Pending Approval": "processing",
      "Approved": "success",
      "Rejected": "error",
      "In Progress": "warning",
      "Completed": "success",
    };
    return statusColors[status] || "default";
  };

  const getRequestForColor = (requestFor: string): string => {
    const colors: { [key: string]: string } = {
      "Construction Work": "blue",
      "Service": "green",
      "Maintenance": "orange",
      "Others": "purple"
    };
    return colors[requestFor] || "default";
  };

  const getPriorityColor = (plannedStartDate: string): string => {
    const today = dayjs();
    const startDate = dayjs(plannedStartDate);
    const daysUntilStart = startDate.diff(today, 'days');

    if (daysUntilStart <= 3) return "error"; // High priority
    if (daysUntilStart <= 7) return "warning"; // Medium priority
    return "success"; // Low priority
  };

  const getPriorityText = (plannedStartDate: string): string => {
    const today = dayjs();
    const startDate = dayjs(plannedStartDate);
    const daysUntilStart = startDate.diff(today, 'days');

    if (daysUntilStart <= 0) return "Overdue";
    if (daysUntilStart <= 3) return "High";
    if (daysUntilStart <= 7) return "Medium";
    return "Low";
  };

  const handleView = (record: WorkRequestData) => {
    window.location.href = `/work-request-view/${record.id}`;
  };

  const handleBulkApprove = async (records: WorkRequestData[]) => {
    const { value: comments }: any = await Swal.fire({
      title: `Approve ${records.length} Work Request(s)`,
      input: "textarea",
      inputLabel: "Approval Comments (Optional)",
      inputPlaceholder: "Enter approval comments...",
      showCancelButton: true,
      confirmButtonText: "Approve All",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
    });

    if (comments !== undefined) {
      let successCount = 0;
      for (const record of records) {
        const updated = updateWorkRequest(record.id, {
          status: WorkRequestStatus.Approved,
          approverComments: comments || undefined
        });
        if (updated) successCount++;
      }

      if (successCount === records.length) {
        Swal.fire("Success!", `${successCount} work request(s) approved successfully.`, "success");
        loadPendingApprovals();
      } else {
        Swal.fire("Partial Success", `${successCount} of ${records.length} work request(s) approved.`, "warning");
        loadPendingApprovals();
      }
    }
  };

  const handleApprove = async (record: WorkRequestData) => {
    const { value: comments } = await Swal.fire({
      title: "Approve Work Request",
      input: "textarea",
      inputLabel: "Approval Comments (Optional)",
      inputPlaceholder: "Enter approval comments...",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
    });

    if (comments !== undefined) {
      const updated: any = updateWorkRequest(record.id, {
        status: WorkRequestStatus.Approved,
        approverComments: comments || undefined
      });
      if (updated) {
        Swal.fire("Approved!", "Work Request has been approved.", "success");
        loadPendingApprovals();
      }
    }
  };

  const handleReject = async (record: WorkRequestData) => {
    // const { value: reason } = await Swal.fire({
    //   title: "Reject Work Request",
    //   input: "textarea",
    //   inputLabel: "Rejection Reason (Required)",
    //   inputPlaceholder: "Enter reason for rejection...",
    //   inputValidator: (value) => {
    //     if (!value) {
    //       return "Rejection reason is required!";
    //     }
    //   },
    //   showCancelButton: true,
    //   confirmButtonText: "Reject",
    //   confirmButtonColor: "#dc3545",
    //   cancelButtonColor: "#6c757d",
    // });

    // if (reason) {
    //   const updated = updateWorkRequest(record.id, {
    //     status: WorkRequestStatus.Rejected,
    //     rejectionComments: reason
    //   });
    //   if (updated) {
    //     Swal.fire("Rejected!", "Work Request has been rejected.", "success");
    //     loadPendingApprovals();
    //   }
    // }
  };

  const columns: TableColumnsType<WorkRequestData> = [
    // {
    //   title: "Priority",
    //   key: "priority",
    //   width: 80,
    //   render: (_, record) => (
    //     <Tag color={getPriorityColor(record.plannedStartDate)}>
    //       {getPriorityText(record.plannedStartDate)}
    //     </Tag>
    //   ),
    //   sorter: (a, b) => {
    //     const aDays = dayjs(a.plannedStartDate).diff(dayjs(), 'days');
    //     const bDays = dayjs(b.plannedStartDate).diff(dayjs(), 'days');
    //     return aDays - bDays;
    //   },
    // },
    {
      title: "WR #",
      dataIndex: "workRequestSequenceId",
      key: "workRequestSequenceId",
      width: 220,
      render: (text) => <strong>{text}</strong>,
      // sorter: (a, b) => a.workRequestNumber.localeCompare(b.workRequestNumber),
    },
    {
      title: "Work Request Title",
      dataIndex: "workRequestTitle",
      key: "workRequestTitle",
      width: 280,
      render: (text, record: any) => {
        const searchParams = new URLSearchParams(location.search);
        const flowType = searchParams.get("flowType");
        const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
        return (
          <div>
            <Link
              to={`/work-request-view/${record.workRequestId}${flowTypeQuery}`}
              className="fw-bold text-primary"
            >
              {text}
            </Link>
            <div className="text-muted small mt-1">
              {record.siteName}
              {record.projectName && ` • ${record.projectName}`}
            </div>
          </div>
        );
      },
    },
    {
      title: "Request For",
      dataIndex: "requestFor",
      key: "requestFor",
      width: 140,
      render: (text) => (
        <Tag color={getRequestForColor(text)}>{text}</Tag>
      ),
    },
    {
      title: "Work Lines",
      dataIndex: "workRequestLinesCount",
      key: "workRequestLinesCount",
      width: 100,
      align: "center",
      render: (text) => (
        <Badge count={text} showZero color="#1890ff" />
        // sorter: (a, b) => a.totalLines - b.totalLines,
      ),
    },
    {
      title: "Planned Start",
      dataIndex: "plannedStartDate",
      key: "plannedStartDate",
      width: 120,
      render: (text) => (
        <div>
          <div className="small">
            {dayjs(text).format("DD-MMM-YYYY")}
          </div>
          {/* <div className="text-muted small">
            {dayjs(text).diff(dayjs(), 'days')} days
          </div> */}
        </div>
      ),
      // sorter: (a, b) => dayjs(a.plannedStartDate).unix() - dayjs(b.plannedStartDate).unix(),
    },
    {
      title: "Duration",
      key: "duration",
      width: 100,
      render: (_, record) => {
        const duration = dayjs(record.plannedEndDate).diff(dayjs(record.plannedStartDate), 'days');
        return <span>{duration} days</span>;
      },
    },
    {
      title: "Submitted By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 140,
      render: (text, record) => (
        <div>
          <div className="small">{text}</div>
          <div className="text-muted small">
            {dayjs(record.createdAt).format("DD-MMM-YYYY")}
          </div>
        </div>
      ),
    },
    {
      title: "Pending Since",
      key: "pendingSince",
      width: 120,
      render: (_, record) => {
        const days = dayjs().diff(dayjs(record.updatedAt || record.createdAt), 'days');
        return (
          <div>
            <div className="small">{days} days</div>
            <div className="text-muted small">
              {dayjs(record.updatedAt || record.createdAt).format("DD-MMM-YYYY")}
            </div>
          </div>
        );
      },
      sorter: (a, b) => {
        const aDays = dayjs().diff(dayjs(a.updatedAt || a.createdAt), 'days');
        const bDays = dayjs().diff(dayjs(b.updatedAt || b.createdAt), 'days');
        return bDays - aDays;
      },
    },
  ];

  const handleFiltersChange = (newFilters: WorkRequestFilters) => {
    setFilters({
      ...newFilters,
      statuses: [WorkRequestStatus.PendingApproval] // Always filter for pending approvals
    });
  };

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: WorkRequestData[]) => {
      // Can be used for bulk operations
    },
    getCheckboxProps: (record: WorkRequestData) => ({
      disabled: record.status !== "Pending Approval",
    }),
  };

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-request-approvals"
      title="Work Request Approvals"
      subtitle="Review and approve pending work requests"
      columns={columns}
      dataSource={pendingApprovals}
      loading={loading}
      // rowSelection={rowSelection}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${sm ? "content-active" : ""
            }`}
        >
          <WorkRequestAside
            updateSm={setSm}
            sm={sm}
            onFiltersChange={handleFiltersChange}
            filters={filters}
          />
        </div>
      }
      // topRightJSX={
      //   <Space>
      //     <ReactstrapButton 
      //       color="success" 
      //       size="sm"
      //       onClick={() => {
      //         // Get selected rows for bulk approve
      //         message.info('Bulk approve functionality - select rows and use the action');
      //       }}
      //     >
      //       <Icon name="check" />
      //       Bulk Approve
      //     </ReactstrapButton>
      //     <Link to="/work-request-creation">
      //       <ReactstrapButton color="primary" size="sm">
      //         <Icon name="plus" />
      //         Create Work Request
      //       </ReactstrapButton>
      //     </Link>
      //   </Space>
      // }
      extra={{
        tableScrollX: 1400,
        showSearch: true,
        searchPlaceholder: "Search pending approvals...",
        onSearch: (value: any) => {
          handleFiltersChange({ ...filters, searchText: value });
        },
        emptyText: "No pending approvals found",
        showRefresh: true,
        onRefresh: loadPendingApprovals
      }}
    />
  );
};

export default WorkRequestApprovals;